import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import { Formik, Form, getIn } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/modules/userSlice";
import { DownloadFormHtmlToPdf, FormQAPI } from "../components/api";
import useDraft from "../hooks/useDraft";
import {
  applySectionDraftData,
  pickSectionDraftData,
} from "../drafts/draftHelpers";
import {
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
} from "../config/joineeFormConstants";
import JoineeFormShell, {
  JoineeFormFooterActions,
} from "../components/NewJoineForm/JoineeFormShell";
import {
  FormSectionTitle,
  FormTextField,
  FormTwoCol,
  FormCheckboxField,
} from "../components/NewJoineForm/JoineeFormFields";
import ApprovedCustomizedDialogs from "../components/Model/ModelApproved";
import {
  canShowHrApprove,
  canShowHrResubmit,
  canShowHrReviewActions,
  isJoineeEmployeeFormLocked,
} from "../config/joineeConstants";
import ReSubmitCustomizedDialogs from "../components/Model/ModelReSubmit";
import ReportValue from "../components/Shared/ReportValue";
import {
  resolveCompanyAddress,
  resolveCompanyName,
} from "../config/companyConfig";
import LoadingButtons from "../components/Model/LoadingButtonModel";
import { areFormQWagesReady } from "../utils/hrFieldAccess";
import { notifyFormInvalid } from "../utils/inputRules";
import HrResubmitNotice from "../components/Joinee/HrResubmitNotice";

var EMPTY_DISPLAY = "—";
var CONSENT_LABEL =
  "I confirm that the information provided is true and accurate, authorize the use of my previously submitted signature, and agree to all applicable terms and conditions.";

function errMsg(formik, path) {
  var touched = getIn(formik.touched, path);
  var error = getIn(formik.errors, path);
  return touched && error ? error : "";
}

function displayValue(value) {
  if (value === null || value === undefined) {
    return EMPTY_DISPLAY;
  }
  var text = String(value).trim();
  return text ? text : EMPTY_DISPLAY;
}

function formatDob(value) {
  if (!value) {
    return EMPTY_DISPLAY;
  }
  var d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY") : displayValue(value);
}

function joinAddressParts(addr) {
  if (!addr) {
    return "";
  }
  var parts = [
    addr.addressLine1,
    addr.addressLine2,
    addr.addressLine3,
    addr.landmark,
    addr.postalCode,
  ];
  return parts
    .map(function (part) {
      return part ? String(part).trim() : "";
    })
    .filter(Boolean)
    .join(", ");
}

function safeFatherName(userResp) {
  if (!userResp || !userResp.father) {
    return "";
  }
  if (typeof userResp.father === "string") {
    return userResp.father;
  }
  return userResp.father.name || "";
}

function fatherOrHusbandName(userResp) {
  var fatherName = safeFatherName(userResp);
  var spouseName =
    userResp && userResp.spouse ? String(userResp.spouse).trim() : "";
  var marital = userResp && userResp.maritalStatus
    ? String(userResp.maritalStatus).toLowerCase()
    : "";
  if (marital === "married" && spouseName) {
    return spouseName;
  }
  return fatherName;
}

function ReadOnlyRow(props) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: "16px", sm: "32px" },
        width: "100%",
        mb: 2,
      }}
    >
      {(props.items || []).map(function (item, index) {
        return (
          <Box
            key={item.label + "-" + index}
            sx={{
              display: "flex",
              flex: "1 1 0",
              gap: "16px",
              alignItems: "flex-start",
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontFamily: JOINEE_FORM_FONT,
                fontSize: 14,
                fontWeight: 400,
                color: "#848F9A",
                lineHeight: 1.14,
                width: { xs: "140px", sm: "164px" },
                flexShrink: 0,
              }}
            >
              {item.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: JOINEE_FORM_FONT,
                fontSize: 14,
                fontWeight: 400,
                color: "#171A1C",
                lineHeight: 1.14,
                minWidth: 0,
                wordBreak: "break-word",
              }}
            >
              {item.value}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function buildValidationSchema(isAdmin) {
  if (isAdmin) {
    return Yup.object({});
  }
  return Yup.object({
    formQLegalConsent: Yup.boolean().oneOf(
      [true],
      "Please confirm that the information provided is true and accurate"
    ),
  });
}

function FormQ(props) {
  var navigate = useNavigate();
  var params = useParams();
  var routeId = params.id ? params.id : "";
  var redux = useSelector(selectUser);
  var user = redux && redux.user ? redux.user : {};
  var isAdmin = Boolean(user.isAdmin);
  var userId = isAdmin ? props.id || routeId : user._id;

  var draftApi = useDraft("formQ", "main");
  var loadDraft = draftApi.loadDraft;
  var saveDraft = draftApi.saveDraft;
  var deleteDraft = draftApi.deleteDraft;

  var [loading, setLoading] = useState(true);
  var [busy, setBusy] = useState(false);
  var [formKey, setFormKey] = useState(0);
  var [initialValues, setInitialValues] = useState(null);
  var [formQFlagButton, setFormQFlagButton] = useState("");
  var [approvedComment, setApprovedComment] = useState("");
  var [reSubmitComment, setReSubmitComment] = useState("");
  var [previewOpen, setPreviewOpen] = useState(false);
  var [previewHtml, setPreviewHtml] = useState("");
  var [previewLoading, setPreviewLoading] = useState(false);
  var [wagesSaved, setWagesSaved] = useState(false);

  useEffect(
    function () {
      var isActive = true;
      if (!userId) {
        setLoading(false);
        return function () {
          isActive = false;
        };
      }

      setLoading(true);
      FormQAPI.getformq(userId)
        .then(function (res) {
          if (!isActive) {
            return;
          }
          var data = res && res.data ? res.data : {};
          var userResp = data.userResp || {};
          var formQData = data.formQData || {};
          var sdf = data.selfDeclarationResp || {};
          var medical = data.medicalInsuranceResp || {};
          var currentAddress = userResp.currentAddress || {};
          var companyName = userResp.companyName || "";
          var todayIso = new Date().toISOString();

          var obj = {
            formQId: formQData._id || "",
            establishment: resolveCompanyName(companyName),
            establishment_address: resolveCompanyAddress(companyName),
            employerName: resolveCompanyName(companyName),
            employeeName: userResp.name || "",
            postalAddress: joinAddressParts(currentAddress),
            fatherName: fatherOrHusbandName(userResp),
            dob: userResp.dateOfBirth || medical.dob || null,
            doj: userResp.doj || null,
            designation: userResp.designation || "",
            basicSalary: formQData.basicSalary || "",
            VDA: formQData.VDA || "",
            allowance: formQData.allowance || "",
            total: formQData.total || "",
            place: sdf.place || formQData.place || "",
            fullName: userResp.name || "",
            emp_dob: todayIso,
            formQLegalConsent: Boolean(formQData.formQLegalConsent),
            formQLegalConsentAt: formQData.formQLegalConsentAt || null,
          };

          setFormQFlagButton(userResp.isFormQComplete || "");
          setWagesSaved(areFormQWagesReady(obj));
          setApprovedComment(
            userResp.internal_notes && userResp.internal_notes.FormQComment
              ? userResp.internal_notes.FormQComment
              : ""
          );
          setReSubmitComment(userResp.FormQComment || "");

          if (isAdmin) {
            setInitialValues(obj);
            setFormKey(function (k) {
              return k + 1;
            });
            setLoading(false);
            return;
          }

          return loadDraft().then(function (draft) {
            if (!isActive) {
              return;
            }
            var merged = applySectionDraftData("formQ", "main", obj, draft);
            merged.establishment = obj.establishment;
            merged.establishment_address = obj.establishment_address;
            merged.employerName = obj.employerName;
            merged.employeeName = obj.employeeName;
            merged.postalAddress = obj.postalAddress;
            merged.fatherName = obj.fatherName;
            merged.dob = obj.dob;
            merged.doj = obj.doj;
            merged.designation = obj.designation;
            merged.place = obj.place;
            merged.fullName = obj.fullName;
            if (!isAdmin) {
              merged.basicSalary = obj.basicSalary;
              merged.VDA = obj.VDA;
              merged.allowance = obj.allowance;
              merged.total = obj.total;
            }
            merged.emp_dob = todayIso;
            setInitialValues(merged);
            setFormKey(function (k) {
              return k + 1;
            });
            setLoading(false);
          });
        })
        .catch(function (err) {
          console.log(err);
          if (isActive) {
            setLoading(false);
            toast.error("Unable to load Form Q");
          }
        });

      return function () {
        isActive = false;
      };
    },
    [userId, isAdmin, loadDraft]
  );

  var showCandidateActions =
    !isAdmin && !isJoineeEmployeeFormLocked(formQFlagButton);

  function payloadFromValues(values) {
    var accepted = Boolean(values.formQLegalConsent);
    return {
      establishment: values.establishment || "",
      establishment_address: values.establishment_address || "",
      basicSalary: String(values.basicSalary || "").trim(),
      VDA: String(values.VDA || "").trim(),
      allowance: String(values.allowance || "").trim(),
      total: String(values.total || "").trim(),
      place: values.place || "",
      formQLegalConsent: accepted,
      formQLegalConsentAt: accepted
        ? values.formQLegalConsentAt || new Date().toISOString()
        : null,
    };
  }

  function persistFormQ(values) {
    var payload = payloadFromValues(values);
    if (values.formQId) {
      return FormQAPI.updateformq(userId, payload).then(function (res) {
        return { res: res, formQId: values.formQId };
      });
    }
    return FormQAPI.createformq(userId, payload).then(function (res) {
      var newId =
        res && res.data && res.data._id ? res.data._id : values.formQId;
      return { res: res, formQId: newId };
    });
  }

  function handleSaveDraft(formik) {
    if (isAdmin) {
      return Promise.resolve();
    }
    var wrapped = pickSectionDraftData("formQ", "main", formik.values);
    setBusy("draft");
    return saveDraft(wrapped)
      .then(function (saved) {
        setBusy(false);
        if (saved && saved.skipped) {
          toast.info("Nothing to save as draft");
          return saved;
        }
        if (saved) {
          toast.success("Draft saved");
          return saved;
        }
        toast.error("Unable to save draft");
        return null;
      })
      .catch(function () {
        setBusy(false);
        toast.error("Unable to save draft");
        return null;
      });
  }

  function handleSaveAndNext(values, formikHelpers) {
    if (isAdmin) {
      if (formikHelpers && formikHelpers.setSubmitting) {
        formikHelpers.setSubmitting(false);
      }
      return;
    }
    setBusy("next");
    persistFormQ(values)
      .then(function (result) {
        if (result && result.formQId && !values.formQId) {
          formikHelpers.setFieldValue("formQId", result.formQId);
        }
        if (
          values.formQLegalConsent &&
          !values.formQLegalConsentAt &&
          formikHelpers.setFieldValue
        ) {
          formikHelpers.setFieldValue(
            "formQLegalConsentAt",
            payloadFromValues(values).formQLegalConsentAt
          );
        }
        return deleteDraft().then(function () {
          return FormQAPI.formqStatus(userId);
        });
      })
      .then(function () {
        setBusy(false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        setFormQFlagButton("submitted");
        toast.success("Form Q submitted");
        navigate("/");
      })
      .catch(function (err) {
        console.log(err);
        setBusy(false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        toast.error("Unable to save Form Q");
      });
  }

  function handleAdminSaveWages(formik) {
    if (!areFormQWagesReady(formik.values)) {
      toast.error("Please fill Basic and Total wage fields before saving");
      return;
    }
    setBusy("admin");
    persistFormQ(formik.values)
      .then(function () {
        return FormQAPI.getformq(userId);
      })
      .then(function (res) {
        var data = res && res.data ? res.data : {};
        var userResp = data.userResp || {};
        setFormQFlagButton(userResp.isFormQComplete || "");
        setBusy(false);
        setWagesSaved(true);
        toast.success("Wage details saved");
      })
      .catch(function () {
        setBusy(false);
        toast.error("Unable to save wage details");
      });
  }

  function canShowAdminReview(values) {
    return (
      canShowHrReviewActions(formQFlagButton) &&
      (wagesSaved || areFormQWagesReady(values))
    );
  }

  function openPreview() {
    if (!userId) {
      return;
    }
    setPreviewLoading(true);
    DownloadFormHtmlToPdf.getform(userId, "FormQFormHtmlData")
      .then(function (res) {
        setPreviewLoading(false);
        if (res && res.success) {
          setPreviewHtml(res.data || "");
          setPreviewOpen(true);
        } else {
          toast.error("Unable to load preview");
        }
      })
      .catch(function () {
        setPreviewLoading(false);
        toast.error("Unable to load preview");
      });
  }

  var formApprovedProps = {
    userId: props.id || routeId,
    formState: "isFormQComplete",
    nextURL: "AdminNda",
    approvedComment: approvedComment,
    setApprovedComment: setApprovedComment,
  };

  var formReSubmitProps = {
    userId: props.id || routeId,
    formState: "isFormQComplete",
    nextURL: "AdminNda",
    reSubmitComment: reSubmitComment,
    setReSubmitComment: setReSubmitComment,
  };

  if (loading || !initialValues) {
    return (
      <Box sx={{ width: "100%", py: 6, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            color: JOINEE_FORM_COLORS.label,
          }}
        >
          Loading Form Q…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      <Formik
        key={formKey}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={buildValidationSchema(isAdmin)}
        onSubmit={handleSaveAndNext}
      >
        {function (formik) {
          var values = formik.values;

          return (
            <Form noValidate>
              <ReportValue
                value={Boolean(isAdmin && canShowAdminReview(values))}
                onChange={props.onReviewReadyChange}
              />
              {isAdmin && !props.hideHrActions && canShowAdminReview(values) ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    mb: 1.5,
                    width: { xs: "100%", md: "82%" },
                    maxWidth: 780,
                    mx: "auto",
                  }}
                >
                  {canShowHrApprove(formQFlagButton) ? (
                    <ApprovedCustomizedDialogs
                      formApprovedProps={formApprovedProps}
                    />
                  ) : null}
                  {canShowHrResubmit(formQFlagButton) ? (
                    <ReSubmitCustomizedDialogs
                      formReSubmitProps={formReSubmitProps}
                    />
                  ) : null}
                </Box>
              ) : null}
              {isAdmin &&
              canShowHrReviewActions(formQFlagButton) &&
              !canShowAdminReview(values) ? (
                <Box
                  sx={{
                    width: { xs: "100%", md: "82%" },
                    maxWidth: 780,
                    mx: "auto",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 13,
                      color: "#848F9A",
                      lineHeight: "20px",
                    }}
                  >
                    Complete and save wage details below to unlock Approve and
                    Re-submit.
                  </Typography>
                </Box>
              ) : null}
              <JoineeFormShell
                banner={
                  !isAdmin && formQFlagButton === "resubmission required" ? (
                    <HrResubmitNotice comment={reSubmitComment} embedded />
                  ) : null
                }
                hideSteps
                onBack={
                  !isAdmin
                    ? function () {
                        navigate("/");
                      }
                    : null
                }
                title="Form Q APPOINTMENT ORDER (See Rule 24(9A))"
                subtitle="Verify the details in your appointment order to complete your employment documentation."
                headerAction={
                  isAdmin ? (
                    previewLoading ? (
                      <LoadingButtons />
                    ) : (
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={openPreview}
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 14,
                          fontWeight: 400,
                          textTransform: "none",
                          color: JOINEE_FORM_COLORS.primary,
                          borderColor: JOINEE_FORM_COLORS.primary,
                          borderRadius: "6px",
                          minHeight: 32,
                          px: "12px",
                          bgcolor: "#FDFDFD",
                        }}
                      >
                        Preview Form
                      </Button>
                    )
                  ) : null
                }
                footer={
                  showCandidateActions ? (
                    <JoineeFormFooterActions
                      showPrevious
                      onPrevious={function () {
                        navigate("/");
                      }}
                      onDraft={function () {
                        return handleSaveDraft(formik);
                      }}
                      onNext={function () {
                        formik.validateForm().then(function (errors) {
                          if (errors && Object.keys(errors).length) {
                            notifyFormInvalid(errors);
                            formik.setTouched(
                              { formQLegalConsent: true },
                              true
                            );
                            return;
                          }
                          formik.handleSubmit();
                        });
                      }}
                      nextLabel="Save and next"
                      disabled={Boolean(busy)}
                      drafting={busy === "draft"}
                      saving={busy === "next"}
                    />
                  ) : null
                }
              >
                <FormSectionTitle>Form Q Info</FormSectionTitle>
                <ReadOnlyRow
                  items={[
                    {
                      label: "Name Of The Establishment:",
                      value: displayValue(values.establishment),
                    },
                    {
                      label: "Address of The Establishment:",
                      value: displayValue(values.establishment_address),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Employer Name:",
                      value: displayValue(values.employerName),
                    },
                    {
                      label: "Name of Employee:",
                      value: displayValue(values.employeeName),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "His/Her Postal Address:",
                      value: displayValue(values.postalAddress),
                    },
                    {
                      label: "Father/Husband Name:",
                      value: displayValue(values.fatherName),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Date of Birth:",
                      value: formatDob(values.dob),
                    },
                    {
                      label: "Date of His/Her Entry into Employment:",
                      value: formatDob(values.doj),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Designation:",
                      value: displayValue(values.designation),
                    },
                  ]}
                />

                {isAdmin ? (
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <FormSectionTitle>
                      Rates of wages payable to him/her
                    </FormSectionTitle>
                    <FormTwoCol>
                      <FormTextField
                        id="formq-basic"
                        name="basicSalary"
                        label="Basic"
                        placeholder="Enter Basic"
                        type="number"
                        value={values.basicSalary}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <FormTextField
                        id="formq-vda"
                        name="VDA"
                        label="VDA"
                        placeholder="Enter VDA"
                        type="number"
                        value={values.VDA}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </FormTwoCol>
                    <FormTwoCol>
                      <FormTextField
                        id="formq-allowance"
                        name="allowance"
                        label="Other Allowance If Any"
                        placeholder="Enter Allowance"
                        type="number"
                        value={values.allowance}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      <FormTextField
                        id="formq-total"
                        name="total"
                        label="Total"
                        placeholder="Enter Total"
                        type="number"
                        value={values.total}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                    </FormTwoCol>
                    <Button
                      type="button"
                      variant="contained"
                      onClick={function () {
                        handleAdminSaveWages(formik);
                      }}
                      disabled={Boolean(busy)}
                      sx={{
                        fontFamily: JOINEE_FORM_FONT,
                        textTransform: "none",
                        bgcolor: JOINEE_FORM_COLORS.primary,
                        boxShadow: "none",
                        mt: 1,
                      }}
                    >
                      Save wage details
                    </Button>
                  </Box>
                ) : null}

                <FormSectionTitle>Acknowledgement By The Employee</FormSectionTitle>
                <ReadOnlyRow
                  items={[
                    {
                      label: "Name:",
                      value: displayValue(values.fullName),
                    },
                    {
                      label: "Place:",
                      value: displayValue(values.place),
                    },
                  ]}
                />
                <ReadOnlyRow
                  items={[
                    {
                      label: "Date:",
                      value: formatDob(values.emp_dob),
                    },
                  ]}
                />

                {!isAdmin ? (
                  <Box
                    sx={{ mt: 1, mb: 1 }}
                    data-ack-error={
                      errMsg(formik, "formQLegalConsent")
                        ? "true"
                        : undefined
                    }
                  >
                    <FormCheckboxField
                      name="formQLegalConsent"
                      checked={Boolean(values.formQLegalConsent)}
                      onChange={function (e) {
                        var checked = Boolean(e.target.checked);
                        formik.setFieldValue(
                          "formQLegalConsent",
                          checked,
                          true
                        );
                        formik.setFieldValue(
                          "formQLegalConsentAt",
                          checked
                            ? values.formQLegalConsentAt ||
                                new Date().toISOString()
                            : null,
                          false
                        );
                        formik.setFieldTouched(
                          "formQLegalConsent",
                          true,
                          false
                        );
                        if (checked) {
                          formik.setFieldError("formQLegalConsent", undefined);
                        }
                      }}
                      label={CONSENT_LABEL}
                      disabled={!showCandidateActions}
                      legal
                    />
                    {errMsg(formik, "formQLegalConsent") &&
                    !values.formQLegalConsent ? (
                      <Typography
                        sx={{
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 12,
                          fontWeight: 400,
                          color: JOINEE_FORM_COLORS.error,
                          mt: 0.5,
                          ml: "28px",
                        }}
                      >
                        {errMsg(formik, "formQLegalConsent")}
                      </Typography>
                    ) : null}
                    <Box
                      sx={{
                        borderBottom:
                          "1px solid " + JOINEE_FORM_COLORS.border,
                        mt: 2,
                        mb: 1,
                      }}
                    />
                  </Box>
                ) : null}
              </JoineeFormShell>
            </Form>
          );
        }}
      </Formik>

      <Dialog
        open={previewOpen}
        onClose={function () {
          setPreviewOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontWeight: 600,
            pr: 6,
          }}
        >
          Preview Form
          <IconButton
            aria-label="close"
            onClick={function () {
              setPreviewOpen(false);
            }}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{ width: "100%", overflowX: "auto" }}
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default FormQ;
